import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

/**
 * Compose the canonical display label for a size triple.
 *  - All three present:           "Petite · UK 10"
 *  - Just system + value:         "UK 10"
 *  - Just fit:                    "Petite"
 *  - System "One Size":           "One Size"
 *  - Anything else:               "" (empty)
 */
export function composeSizeLabel({ fit, system, value }) {
  const F = (fit || "").trim();
  const S = (system || "").trim();
  const V = (value || "").trim();
  if (S && V) {
    if (S.toLowerCase() === "one size") return V || S;
    return F && F.toLowerCase() !== "regular" ? `${F} · ${S} ${V}` : `${S} ${V}`;
  }
  if (F) return F;
  return "";
}

/**
 * SizePicker — three coordinated dropdowns (Fit · System · Value) bound to the
 * managed /api/size_systems catalog. Emits both the structured triple and the
 * composed display label.
 *
 * Props:
 *  - fit / system / value: current structured selection
 *  - onChange({fit, system, value, label}): emits the next triple + composed label
 *  - canManage: shows the "manage" button (admin)
 *  - onManagerClose(): optional callback fired when the manager dialog closes
 */
export default function SizePicker({ fit, system, value, onChange, canManage = true, onManagerClose }) {
  const [catalog, setCatalog] = useState({ fits: [], systems: [] });
  const [loading, setLoading] = useState(false);
  const [showManager, setShowManager] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/size_systems");
      setCatalog({ fits: data.fits || [], systems: data.systems || [] });
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("size_systems load failed", e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeManager = () => {
    setShowManager(false);
    load();
    if (onManagerClose) onManagerClose();
  };

  const emit = (next) => {
    const merged = { fit, system, value, ...next };
    onChange({ ...merged, label: composeSizeLabel(merged) });
  };

  const activeSystem = catalog.systems.find((s) => s.label === system);
  // Carry legacy values that no longer appear in the active system.
  const valueOptions = activeSystem ? activeSystem.values : [];
  const showLegacyValue = value && !valueOptions.includes(value);
  const showLegacyFit = fit && !catalog.fits.includes(fit);
  const showLegacySystem = system && !catalog.systems.some((s) => s.label === system);

  return (
    <div className="md:col-span-2">
      <div className="flex items-center justify-between mb-2">
        <label className="editorial-label">size</label>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowManager(true)}
            data-testid="manage-sizes-btn"
            className="text-[0.6rem] tracking-widest uppercase border border-taupe text-charcoal hover:border-brass hover:text-brass px-2 py-1"
          >
            manage
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="text-[0.65rem] tracking-widest uppercase text-taupe">fit</label>
          <select
            value={fit || ""}
            onChange={(e) => emit({ fit: e.target.value })}
            data-testid="size-fit"
            className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none text-charcoal"
          >
            <option value="">— any —</option>
            {catalog.fits.map((f) => <option key={f} value={f}>{f.toLowerCase()}</option>)}
            {showLegacyFit && <option value={fit}>{fit} (legacy)</option>}
          </select>
        </div>
        <div>
          <label className="text-[0.65rem] tracking-widest uppercase text-taupe">system</label>
          <select
            value={system || ""}
            onChange={(e) => emit({ system: e.target.value, value: "" })}
            data-testid="size-system"
            className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none text-charcoal"
          >
            <option value="">— select —</option>
            {catalog.systems.map((s) => <option key={s.label} value={s.label}>{s.label.toLowerCase()}</option>)}
            {showLegacySystem && <option value={system}>{system} (legacy)</option>}
          </select>
        </div>
        <div>
          <label className="text-[0.65rem] tracking-widest uppercase text-taupe">value</label>
          <select
            value={value || ""}
            onChange={(e) => emit({ value: e.target.value })}
            data-testid="size-value"
            disabled={!system}
            className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none text-charcoal disabled:opacity-40"
          >
            <option value="">— select —</option>
            {valueOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            {showLegacyValue && <option value={value}>{value} (legacy)</option>}
          </select>
        </div>
      </div>
      {composeSizeLabel({ fit, system, value }) && (
        <p className="text-[0.65rem] tracking-widest uppercase text-brass mt-2" data-testid="size-composed">
          → {composeSizeLabel({ fit, system, value })}
        </p>
      )}
      {loading && <p className="text-[0.6rem] text-taupe mt-1 tracking-widest uppercase">loading…</p>}
      {showManager && <SizeManagerDialog onClose={closeManager} />}
    </div>
  );
}

function SizeManagerDialog({ onClose }) {
  const [fits, setFits] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newFit, setNewFit] = useState("");
  const [newSystemLabel, setNewSystemLabel] = useState("");
  const [newValueByIdx, setNewValueByIdx] = useState({}); // {systemIdx: draftString}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/size_systems");
      setFits(data.fits || []);
      setSystems(data.systems || []);
    } catch (e) {
      toast.error("could not load size systems.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const commit = async (nextFits, nextSystems) => {
    try {
      const { data } = await api.put("/size_systems", { fits: nextFits, systems: nextSystems });
      setFits(data.fits || []);
      setSystems(data.systems || []);
    } catch (e) {
      toast.error("could not save.");
    }
  };

  const addFit = () => {
    const v = newFit.trim();
    if (!v) return;
    if (fits.some((f) => f.toLowerCase() === v.toLowerCase())) {
      toast.error("already in the list.");
      return;
    }
    const next = [...fits, v];
    setFits(next);
    setNewFit("");
    commit(next, systems);
  };

  const removeFit = (i) => {
    const next = fits.filter((_, idx) => idx !== i);
    setFits(next);
    commit(next, systems);
  };

  const addSystem = () => {
    const label = newSystemLabel.trim();
    if (!label) return;
    if (systems.some((s) => s.label.toLowerCase() === label.toLowerCase())) {
      toast.error("system already exists.");
      return;
    }
    const next = [...systems, { label, values: [] }];
    setSystems(next);
    setNewSystemLabel("");
    commit(fits, next);
  };

  const removeSystem = (i) => {
    const next = systems.filter((_, idx) => idx !== i);
    setSystems(next);
    commit(fits, next);
  };

  const addValue = (idx) => {
    const v = (newValueByIdx[idx] || "").trim();
    if (!v) return;
    if (systems[idx].values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      toast.error("value already in this system.");
      return;
    }
    const next = systems.map((s, i) => i === idx ? { ...s, values: [...s.values, v] } : s);
    setSystems(next);
    setNewValueByIdx({ ...newValueByIdx, [idx]: "" });
    commit(fits, next);
  };

  const removeValue = (idx, vi) => {
    const next = systems.map((s, i) => i === idx ? { ...s, values: s.values.filter((_, j) => j !== vi) } : s);
    setSystems(next);
    commit(fits, next);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-auto"
      onClick={onClose}
      data-testid="size-manager-dialog"
    >
      <div className="bg-ivory border border-taupe max-w-3xl w-full p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="editorial-label">taxonomy · sizes</span>
            <h2 className="font-serif text-3xl lowercase text-charcoal mt-2">manage sizes</h2>
            <p className="text-xs text-espresso mt-1">edit the global fit list and add measurement systems with their value scales (EU/US/UK/letter/…).</p>
          </div>
          <button onClick={onClose} aria-label="close" className="text-taupe hover:text-charcoal p-1" data-testid="sz-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && <p className="text-xs text-taupe italic">loading…</p>}

        <div className="grid md:grid-cols-2 gap-8 mt-6">
          {/* FITS column */}
          <div>
            <p className="editorial-label mb-3">fits</p>
            <ul className="space-y-2 mb-3" data-testid="sz-fits">
              {fits.map((f, i) => (
                <li key={f} className="flex items-center justify-between border-b border-taupe/40 pb-1.5" data-testid={`sz-fit-${i}`}>
                  <span className="text-sm lowercase text-charcoal">{f}</span>
                  <button onClick={() => removeFit(i)} className="p-1 border border-taupe hover:border-red-700 hover:text-red-700" aria-label="remove" data-testid={`sz-fit-rm-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newFit}
                onChange={(e) => setNewFit(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFit(); } }}
                placeholder="e.g. cropped"
                data-testid="sz-fit-new"
                className="flex-1 bg-transparent border-b border-taupe py-1 text-sm text-charcoal focus:outline-none focus:border-brass"
              />
              <button onClick={addFit} className="border border-taupe text-charcoal hover:border-brass hover:text-brass p-1.5" aria-label="add fit" data-testid="sz-fit-add">
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* SYSTEMS column */}
          <div>
            <p className="editorial-label mb-3">systems</p>
            <ul className="space-y-3 mb-3 max-h-[55vh] overflow-auto" data-testid="sz-systems">
              {systems.map((s, i) => (
                <li key={s.label} className="border border-taupe/40 p-2" data-testid={`sz-sys-${s.label}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm lowercase text-charcoal">{s.label}</span>
                    <button onClick={() => removeSystem(i)} className="p-1 border border-taupe hover:border-red-700 hover:text-red-700" aria-label="remove system" data-testid={`sz-sys-rm-${s.label}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {s.values.map((v, vi) => (
                      <span key={v} className="inline-flex items-center gap-1 border border-taupe text-[0.7rem] px-2 py-0.5 text-charcoal" data-testid={`sz-val-${s.label}-${v}`}>
                        {v}
                        <button onClick={() => removeValue(i, vi)} aria-label="remove value" className="text-taupe hover:text-red-700" data-testid={`sz-val-rm-${s.label}-${v}`}>
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newValueByIdx[i] || ""}
                      onChange={(e) => setNewValueByIdx({ ...newValueByIdx, [i]: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addValue(i); } }}
                      placeholder="new value"
                      data-testid={`sz-val-new-${s.label}`}
                      className="flex-1 bg-transparent border-b border-taupe py-1 text-xs text-charcoal focus:outline-none focus:border-brass"
                    />
                    <button onClick={() => addValue(i)} className="border border-taupe text-charcoal hover:border-brass hover:text-brass p-1" aria-label="add value" data-testid={`sz-val-add-${s.label}`}>
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newSystemLabel}
                onChange={(e) => setNewSystemLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSystem(); } }}
                placeholder="new system (e.g. KR)"
                data-testid="sz-sys-new"
                className="flex-1 bg-transparent border-b border-taupe py-1 text-sm text-charcoal focus:outline-none focus:border-brass"
              />
              <button onClick={addSystem} className="border border-taupe text-charcoal hover:border-brass hover:text-brass p-1.5" aria-label="add system" data-testid="sz-sys-add">
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-taupe/40 mt-8 pt-4 flex justify-end">
          <button onClick={onClose} data-testid="sz-done" className="border border-taupe text-charcoal hover:border-brass hover:text-brass px-5 py-2 text-xs tracking-[0.25em] uppercase">
            done
          </button>
        </div>
      </div>
    </div>
  );
}
